import { z } from "zod";
import { marketIdSchema } from "./market";

/**
 * A tradable instrument, independent of any provider's catalog.
 *
 * Every adapter maps its vendor's asset representation onto this. Nothing above
 * `packages/registry` ever sees a vendor's asset shape.
 */

/**
 * `equity`, not `us_equity`.
 *
 * The country was in the asset class only because there was nowhere else to put
 * it; now there is a market. Alpaca's surface still answers `us_equity` because
 * that is Alpaca's vocabulary, and translating a vendor's spelling is what
 * `packages/dialect-alpaca` is for — the compat suite is the check that this
 * rename did not leak outward.
 */
export const ASSET_CLASSES = ["equity", "etf", "crypto", "option", "future"] as const;
export type AssetClass = (typeof ASSET_CLASSES)[number];

export const assetClassSchema = z.enum(ASSET_CLASSES);

/**
 * Symbols are uppercase everywhere, normalized at every boundary.
 *
 * Vendors disagree: some accept lowercase, some return mixed case, some are
 * case-sensitive. Normalizing once at the edge means `SymbolBookDO` routing,
 * Ably channel names (`md:{SYMBOL}`), and ledger account names
 * (`position:{SYMBOL}`) can never disagree about which instrument they mean.
 */
export const symbolSchema = z
  .string()
  .min(1)
  .max(16)
  .regex(/^[A-Za-z0-9.\-/]+$/, "symbol contains unsupported characters")
  .transform((s) => s.toUpperCase());

export function normalizeSymbol(symbol: string): string {
  return symbolSchema.parse(symbol);
}

export const instrumentSchema = z.object({
  symbol: symbolSchema,
  /**
   * The venue it trades on. Both markets list `AAPL`, so a symbol on its own
   * does not identify an instrument — and pretending otherwise is what would
   * give a SIM account and a US account one order book with two prices.
   */
  market: marketIdSchema.default("SIM"),
  name: z.string(),
  assetClass: assetClassSchema,
  exchange: z.string(),
  tradable: z.boolean(),
  shortable: z.boolean().default(false),
  fractionable: z.boolean().default(false),
  /** Minimum price movement, as a decimal string. "0.01" for most US equities. */
  tickSize: z.string().default("0.01"),
  /** Minimum order size, as a decimal string. "1" for whole shares. */
  lotSize: z.string().default("1"),
});

export type Instrument = z.infer<typeof instrumentSchema>;
